import resolveConfig from 'tailwindcss/resolveConfig'
import _ from 'lodash'
import moment from 'moment'
import numeral from 'numeral'
import { BREAKPOINTS } from '@/constants'

export const tailwindConfig = () =>
    // Tailwind config
    resolveConfig('./src/css/tailwind.config.js')

export const hexToRGB = (h) => {
    let r = 0
    let g = 0
    let b = 0
    if (h.length === 4) {
        r = `0x${h[1]}${h[1]}`
        g = `0x${h[2]}${h[2]}`
        b = `0x${h[3]}${h[3]}`
    } else if (h.length === 7) {
        r = `0x${h[1]}${h[2]}`
        g = `0x${h[3]}${h[4]}`
        b = `0x${h[5]}${h[6]}`
    }
    return `${+r},${+g},${+b}`
}

export const formatValue = (value) =>
    Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumSignificantDigits: 3,
        notation: 'compact',
    }).format(value)

export const formatThousands = (value) =>
    Intl.NumberFormat('en-US', {
        maximumSignificantDigits: 3,
        notation: 'compact',
    }).format(value)

export const formatDateAndTime = (datetime) => {
    return datetime ? moment(datetime).format('MMM Do, YYYY h:mm A') : ''
}

/**
 * @typedef {'xs'|'sm'|'md'|'lg'|'xl'|'2xl'} ScreenSize
 */

export const screenSize = {
    /**
     * Get current screen size
     *
     * @returns {(ScreenSize|null)} - Screen size label, or `null` if unable to determine.
     * - `'xs'`: Extra small (less than 640px)
     * - `'sm'`: Small (640px and up)
     * - `'md'`: Medium (768px and up)
     * - `'lg'`: Large (1024px and up)
     * - `'xl'`: Extra large (1280px and up)
     * - `'2xl'`: 2X large (1536px and up)
     */
    getCurrent() {
        const width = window.innerWidth

        if (width < BREAKPOINTS.sm) return 'xs'
        if (width < BREAKPOINTS.md) return 'sm'
        if (width < BREAKPOINTS.lg) return 'md'
        if (width < BREAKPOINTS.xl) return 'lg'
        if (width < BREAKPOINTS['2xl']) return 'xl'
        if (width >= BREAKPOINTS['2xl']) return '2xl'

        return null
    },
    /**
     * @param {ScreenSize} bp
     * @returns {boolean}
     */
    isMoreOrEqualThan: (bp) => BREAKPOINTS[bp] <= window.innerWidth,
    /**
     * @param {ScreenSize} bp
     * @returns {boolean}
     */
    isLessThan: (bp) => BREAKPOINTS[bp] > window.innerWidth,
}

export const formatWholeNumberWithUnit = (input) => {
    // eslint-disable-next-line no-param-reassign
    input = Number(input)
    if (!input) return '0'
    if (input >= 1000 && input % 1000 === 0) return numeral(input).format('0a')
    if (input >= 1000) return numeral(input).format('0.0a').replace('.0', '')
    if (input < 1 && input > 0) return numeral(input).format('0.0')
    return numeral(input).format('0')
}

/**
 * This code is licensed under the terms of the MIT license
 *
 * Deep diff between two object, using lodash
 * @param  {Object} object Object compared
 * @param  {Object} base   Object to compare with
 * @return {Object}        Return a new object who represent the diff
 */
export function difference(object, base) {
    function changes(_object, _base) {
        return _.transform(_object, (result, value, key) => {
            if (!_.isEqual(value, _base[key])) {
                result[key] = _.isObject(value) && _.isObject(_base[key]) ? changes(value, _base[key]) : value
            }
        })
    }
    return changes(object, base)
}

export const getMarketRate = (superview, deliverableType, windowKey) => {
    if (deliverableType === 'youtube-video') {
        const conversionMetric = 50 // CPM

        const dropoffMultiplier =
            1 + _.get(superview, `video.${windowKey}.accumulation.games.performance.non_main.median`)
        const averageKPI = _.get(superview, `video.${windowKey}.aggregation.views.avg`)
        const estimatedKPI = averageKPI * dropoffMultiplier

        return {
            rate: (estimatedKPI / 1000) * conversionMetric,
            unit: 'per video',
            conversionMetric,
            dropoffMultiplier,
            averageKPI,
        }
    }

    if (deliverableType === 'youtube-video-integration') {
        const conversionMetric = 50 / 3 // CPM

        const dropoffMultiplier =
            1 + _.get(superview, `video.${windowKey}.accumulation.games.performance.non_main.median`)
        const averageKPI = _.get(superview, `video.${windowKey}.aggregation.views.avg`)
        const estimatedKPI = averageKPI * dropoffMultiplier

        return {
            rate: (estimatedKPI / 1000) * conversionMetric,
            unit: 'per video',
            conversionMetric,
            dropoffMultiplier,
            averageKPI,
        }
    }

    if (deliverableType === 'youtube-short') {
        const conversionMetric = 5 // CPM

        const dropoffMultiplier =
            1 + _.get(superview, `short.${windowKey}.accumulation.games.performance.non_main.median`)
        const averageKPI = _.get(superview, `short.${windowKey}.aggregation.views.avg`)
        const estimatedKPI = averageKPI * dropoffMultiplier

        return {
            rate: (estimatedKPI / 1000) * conversionMetric,
            unit: 'per short',
            conversionMetric,
            dropoffMultiplier,
            averageKPI,
        }
    }

    if (deliverableType === 'twitch-stream') {
        const conversionMetric = 1.25 // CPACV

        const dropoffMultiplier =
            1 + _.get(superview, `stream.${windowKey}.accumulation.games.performance.non_main.median`)
        const averageKPI = _.get(superview, `stream.${windowKey}.aggregation.views.avg`)
        const estimatedKPI = averageKPI * dropoffMultiplier

        return {
            rate: estimatedKPI * conversionMetric,
            unit: 'per hour',
            conversionMetric,
            dropoffMultiplier,
            averageKPI,
        }
    }

    return false
}

export const getPlatformOrder = (platformIds) => {
    const order = ['youtube', 'twitch', 'tiktok', 'twitter', 'instagram']
    return _.sortBy(platformIds, (platformId) => order.indexOf(platformId))
}
